import { Cloth, type Capsule } from './cloth';
import { clamp, Vec3 } from '../core/math';
import {
  MASTS, SAILS, SAIL_PLANS, mast, mastXAt, sailMount, yard,
  type ForeAftSailSpec, type MastId, type SailPlan, type SailSpec, type SquareSailSpec,
} from '../data/ship';

// Sails: aerodynamics for the ship (analytic lift/drag per sail, which gets
// upwind performance right) plus a cloth simulation per sail for the visuals
// and luffing, driven by the same apparent wind.

const AIR = 1.225;
/** Maximum yard brace either way (the shrouds stop the yards). */
export const MAX_BRACE = 0.62;
export const MAX_SHEET = 1.3;
/** Yards hang this far forward of the mast axis. */
const SLING_OFFSET = 0.55;

export interface SailState {
  spec: SailSpec;
  mast: MastId | 'bowsprit';
  deploy: number;
  target: number;
  cloth: Cloth;
  /** Full area, m^2. */
  area: number;
  /** Centre of effort (ship-local) at full deploy. */
  ce: Vec3;
  /** Aerodynamic force on the ship this step (ship-local, N). */
  force: Vec3;
  /** Angle of attack, rad (0 = luffing, PI/2 = square to wind). */
  aoa: number;
  /** Negative when the wind is on the wrong face (aback). */
  filled: number;
  /** Previous fill state, for "sail fills" events. */
  wasFilled: boolean;
  /** Which mast and level carry it (for dismasting). */
  mount: { mast: MastId; level: number };
  /** Carried away with its mast or yard. */
  lost: boolean;
  /** How badly it has been reported shot through: 0 not, 1 half gone, 2 in ribbons. */
  torn: 0 | 1 | 2;
  /** Sailmakers' work done toward the next stitch (in particles of cloth). */
  stitch: number;
}

export interface FlagState {
  cloth: Cloth;
  /** Hoist line (ship-local). */
  top: Vec3;
  bottom: Vec3;
  lost?: boolean;
}

function lift(alpha: number, square: boolean): number {
  const clMax = square ? 1.05 : 1.3;
  const a0 = square ? 0.5 : 0.36;
  if (alpha <= a0) return clMax * Math.sin((Math.PI * alpha) / (2 * a0));
  const s = (alpha - a0) / (Math.PI / 2 - a0);
  return clMax * (0.8 * Math.cos(s * Math.PI * 0.5) + 0.2 * (1 - s));
}

function drag(alpha: number, square: boolean): number {
  const cd0 = square ? 0.1 : 0.05;
  const cd90 = square ? 1.35 : 1.15;
  const s = Math.sin(alpha);
  return cd0 + (cd90 - cd0) * s * s;
}

/**
 * Aerodynamic force (ship-local, horizontal) of a sail with horizontal unit
 * normal (nx, nz) in apparent wind (ax, az). Writes into out; returns AoA.
 */
export function sailForce(ax: number, az: number, nx: number, nz: number, area: number, square: boolean, out: Vec3): number {
  const speed = Math.hypot(ax, az);
  out.set(0, 0, 0);
  if (speed < 0.05 || area <= 0) return 0;
  const dx = ax / speed, dz = az / speed;
  const an = dx * nx + dz * nz;
  const alpha = Math.asin(clamp(Math.abs(an), 0, 1));
  const q = 0.5 * AIR * speed * speed * area;
  const cl = lift(alpha, square);
  const cd = drag(alpha, square);
  // Lift is perpendicular to the wind, toward the side the sail bellies to.
  let lx = -dz, lz = dx;
  const side = Math.sign(an) || 1;
  if ((lx * nx + lz * nz) * side < 0) {
    lx = -lx;
    lz = -lz;
  }
  out.set(q * (cl * lx + cd * dx), 0, q * (cl * lz + cd * dz));
  return alpha;
}

/** Point on a yard braced by `brace`, s metres from its centre (starboard +). */
export function yardPoint(yardId: string, brace: number, s: number, out: Vec3): Vec3 {
  const y = yard(yardId);
  const m = mast(y.mast);
  const mx = mastXAt(m, y.y);
  const c = Math.cos(brace), sn = Math.sin(brace);
  return out.set(mx + SLING_OFFSET * c + s * sn, y.y, -SLING_OFFSET * sn + s * c);
}

/** Rotate a ship-local point about the vertical axis through (px, pz). */
function rotateAbout(x: number, z: number, px: number, pz: number, a: number, out: Vec3, y: number): Vec3 {
  const dx = x - px, dz = z - pz;
  const c = Math.cos(a), s = Math.sin(a);
  return out.set(px + dx * c + dz * s, y, pz - dx * s + dz * c);
}

/** Rotate point p about the axis through a-b by angle (Rodrigues). */
function rotateAboutAxis(p: Vec3, a: Vec3, b: Vec3, ang: number, out: Vec3): Vec3 {
  const kx0 = b.x - a.x, ky0 = b.y - a.y, kz0 = b.z - a.z;
  const kl = Math.hypot(kx0, ky0, kz0) || 1;
  const kx = kx0 / kl, ky = ky0 / kl, kz = kz0 / kl;
  const vx = p.x - a.x, vy = p.y - a.y, vz = p.z - a.z;
  const c = Math.cos(ang), s = Math.sin(ang);
  const dot = kx * vx + ky * vy + kz * vz;
  const cx = ky * vz - kz * vy, cy = kz * vx - kx * vz, cz = kx * vy - ky * vx;
  return out.set(
    a.x + vx * c + cx * s + kx * dot * (1 - c),
    a.y + vy * c + cy * s + ky * dot * (1 - c),
    a.z + vz * c + cz * s + kz * dot * (1 - c),
  );
}

export class SailRig {
  readonly sails: SailState[] = [];
  readonly flags: FlagState[] = [];
  /** Brace angle per mast (square sails). */
  readonly brace: Record<MastId, number> = { fore: 0, main: 0, mizzen: 0 };
  readonly braceTarget: Record<MastId, number> = { fore: 0, main: 0, mizzen: 0 };
  /** Sheet angle per fore-and-aft sail id. */
  readonly sheet: Record<string, number> = {};
  private readonly sheetTarget: Record<string, number> = {};
  plan = 3;
  readonly plans: readonly SailPlan[];
  /** Masts this rig still has standing. */
  readonly masts: MastId[];
  /** Colours hauled down (surrender). */
  struck = false;
  private strikeT = 0;
  /** Crew available to handle sail, 0..1 (scales how fast sails are set). */
  handling = 1;
  autoTrim = true;
  /** Manual trim (-1..1) applied when autoTrim is off. */
  manualTrim = 0;
  private time = 0;
  private readonly tmp = new Vec3();
  private readonly tmp2 = new Vec3();
  private readonly tmpF = new Vec3();
  private readonly trimF = new Vec3();
  private readonly trimW = new Vec3();
  private readonly byMast: Record<MastId, SailState[]> = { fore: [], main: [], mizzen: [] };

  constructor(initialPlan = 3, sailIds: readonly string[] | null = null, plans: readonly SailPlan[] = SAIL_PLANS, masts: readonly MastId[] = ['fore', 'main', 'mizzen']) {
    this.plan = initialPlan;
    this.plans = plans;
    this.masts = [...masts];
    for (const spec of SAILS) {
      if (sailIds && !sailIds.includes(spec.id)) continue;
      const target = plans[initialPlan]!.deploy[spec.id] ?? 0;
      if (spec.kind === 'foreaft') {
        this.sheet[spec.id] = 0;
        this.sheetTarget[spec.id] = 0;
      }
      const cloth = spec.kind === 'square' ? this.makeSquare(spec) : this.makeForeAft(spec);
      const { area, ce } = sailGeometry(spec);
      this.sails.push({
        spec,
        mast: spec.kind === 'square' ? yard(spec.yard).mast : spec.peak ? sailMount(spec.id).mast : 'bowsprit',
        deploy: target,
        target,
        cloth,
        area,
        ce,
        force: new Vec3(),
        aoa: 0,
        filled: 0,
        wasFilled: target > 0.5,
        torn: 0,
        stitch: 0,
        mount: sailMount(spec.id),
        lost: false,
      });
    }
    for (const s of this.sails) if (s.spec.kind === 'square') this.byMast[s.mast as MastId].push(s);
    this.makeFlags();
    this.pinAll(true);
  }

  get planName(): string {
    return this.plans[this.plan]!.name;
  }

  setPlan(i: number): void {
    this.plan = clamp(Math.round(i), 0, this.plans.length - 1);
    for (const s of this.sails) s.target = this.plans[this.plan]!.deploy[s.spec.id] ?? 0;
  }

  /** Sails carried away when a mast breaks at `level` (0 = lower mast). */
  loseMast(id: MastId, level: number): SailState[] {
    const lost: SailState[] = [];
    for (const s of this.sails) {
      if (s.lost || s.mount.mast !== id || s.mount.level < level) continue;
      s.lost = true;
      s.deploy = 0;
      s.target = 0;
      s.force.set(0, 0, 0);
      lost.push(s);
    }
    if (level === 0) {
      const i = this.masts.indexOf(id);
      if (i >= 0) this.masts.splice(i, 1);
    }
    return lost;
  }

  /**
   * A spare spar sent up (or a jury mast stepped): the mast now stands to
   * `lostLevel` (0 whole .. 2 only the lower mast), and the canvas it carries
   * again is bent on new from the spare sails, furled until the plan sets it.
   */
  restoreMast(id: MastId, lostLevel: number): SailState[] {
    const back: SailState[] = [];
    if (lostLevel < 3 && !this.masts.includes(id)) {
      const order: MastId[] = ['fore', 'main', 'mizzen'];
      const at = this.masts.findIndex((m) => order.indexOf(m) > order.indexOf(id));
      this.masts.splice(at < 0 ? this.masts.length : at, 0, id);
    }
    for (const s of this.sails) {
      if (!s.lost || s.mount.mast !== id || (lostLevel > 0 && s.mount.level >= 3 - lostLevel)) continue;
      s.lost = false;
      s.torn = 0;
      s.stitch = 0;
      s.cloth.mend();
      s.deploy = 0;
      s.target = this.struck ? 0 : this.plans[this.plan]!.deploy[s.spec.id] ?? 0;
      if (s.spec.kind === 'square') this.pinSquare(s, s.spec, true);
      else this.pinForeAft(s, s.spec, true);
      back.push(s);
    }
    if (id === 'main' && lostLevel === 0) {
      for (const f of this.flags) {
        f.lost = false;
        f.cloth.mend();
      }
    }
    return back;
  }

  /** Dev: the masts restepped, every sail and flag bent on new, set to the plan. */
  restore(masts: readonly MastId[]): void {
    this.masts.length = 0;
    this.masts.push(...masts);
    for (const s of this.sails) {
      s.lost = false;
      s.torn = 0;
      s.stitch = 0;
      s.cloth.mend();
    }
    for (const f of this.flags) {
      f.lost = false;
      f.cloth.mend();
    }
    this.setPlan(this.plan);
    this.pinAll(true);
  }

  /** Mean effectiveness of the set canvas (torn sails draw less). */
  get sailHealth(): number {
    let a = 0, h = 0;
    for (const s of this.sails) {
      a += s.area;
      h += s.lost ? 0 : s.area * s.cloth.health;
    }
    return a > 0 ? h / a : 0;
  }

  /** Total set canvas, m^2. */
  get setArea(): number {
    let a = 0;
    for (const s of this.sails) a += s.area * s.deploy;
    return a;
  }

  private makeSquare(spec: SquareSailSpec): Cloth {
    const y = yard(spec.yard);
    const m = mast(y.mast);
    const cloth = new Cloth(spec.cols, spec.rows, (u, v, out) => {
      const top = (u * 2 - 1) * y.half * 0.97;
      const bot = (u * 2 - 1) * spec.footHalf;
      const yy = y.y + (spec.footY - y.y) * v;
      out[0] = mastXAt(m, yy) + SLING_OFFSET + 0.1;
      out[1] = yy;
      out[2] = top + (bot - top) * v;
    }, 0.95);
    cloth.colliders = [mastCapsule(y.mast, spec.footY - 1, y.y + 1)];
    return cloth;
  }

  private makeForeAft(spec: ForeAftSailSpec): Cloth {
    return new Cloth(spec.cols, spec.rows, (u, v, out) => {
      const p = foreAftPoint(spec, u, v, 0, 1, this.tmp);
      out[0] = p.x;
      out[1] = p.y;
      out[2] = p.z;
    }, 0.8);
  }

  private makeFlags(): void {
    const main = mast('main');
    const topY = (main.topgallantTop ?? main.topmastTop) + 0.2;
    const x = mastXAt(main, topY) - 0.1;
    // Colours flown at the main truck.
    const flagTop = new Vec3(x, topY + 1.9, 0);
    const flagBottom = new Vec3(x, topY + 0.1, 0);
    const flag = new Cloth(11, 8, (u, v, out) => {
      out[0] = x - u * 2.8;
      out[1] = flagTop.y + (flagBottom.y - flagTop.y) * v;
      out[2] = 0;
    }, 0.35);
    this.flags.push({ cloth: flag, top: flagTop, bottom: flagBottom });
    // Long pennant at the fore truck.
    const fore = mast('fore');
    const fy = (fore.topgallantTop ?? fore.topmastTop) + 0.3;
    const fx = mastXAt(fore, fy) - 0.1;
    const pTop = new Vec3(fx, fy + 0.45, 0);
    const pBottom = new Vec3(fx, fy, 0);
    const pennant = new Cloth(14, 3, (u, v, out) => {
      out[0] = fx - u * 6.5;
      out[1] = pTop.y + (pBottom.y - pTop.y) * v * (1 - u * 0.8) - u * 0.2;
      out[2] = 0;
    }, 0.25);
    this.flags.push({ cloth: pennant, top: pTop, bottom: pBottom });
  }

  /** Pin cloth particles to their spars for the current brace / sheet / deploy. */
  private pinAll(teleport: boolean): void {
    for (const s of this.sails) {
      if (s.spec.kind === 'square') this.pinSquare(s, s.spec, teleport);
      else this.pinForeAft(s, s.spec, teleport);
    }
    // Struck colours come down the halyard.
    const drop = this.strikeT * 14;
    for (const f of this.flags) {
      const c = f.cloth;
      for (let r = 0; r < c.rows; r++) {
        const v = r / (c.rows - 1);
        c.pin(r * c.cols, f.top.x, f.top.y + (f.bottom.y - f.top.y) * v - drop, f.top.z, teleport);
      }
    }
  }

  private pinSquare(s: SailState, spec: SquareSailSpec, teleport: boolean): void {
    const c = s.cloth;
    const y = yard(spec.yard);
    const b = this.brace[y.mast];
    const d = s.deploy;
    for (let col = 0; col < c.cols; col++) {
      const u = col / (c.cols - 1);
      const p = yardPoint(spec.yard, b, (u * 2 - 1) * y.half * 0.97, this.tmp);
      c.pin(col, p.x, p.y + y.radius, p.z, teleport);
    }
    const last = (c.rows - 1) * c.cols;
    for (const [i, sgn] of [[last, -1], [last + c.cols - 1, 1]] as const) {
      // Clew: sheeted to the yard below (or the deck for courses), hauled up
      // to the yard when the sail is clewed up.
      let fx: number, fy: number, fz: number;
      if (spec.footYard) {
        const fy0 = yard(spec.footYard);
        const p = yardPoint(spec.footYard, this.brace[fy0.mast], sgn * Math.min(spec.footHalf, fy0.half * 0.96), this.tmp);
        fx = p.x;
        fy = p.y + fy0.radius;
        fz = p.z;
      } else {
        const m = mast(y.mast);
        const px = mastXAt(m, spec.footY);
        const p = rotateAbout(px + SLING_OFFSET + 0.5, sgn * spec.footHalf, px, 0, b * 0.8, this.tmp, spec.footY);
        fx = p.x - 0.8;
        fy = p.y;
        fz = p.z;
      }
      const up = yardPoint(spec.yard, b, sgn * y.half * 0.35, this.tmp2);
      const k = Math.min(1, d * 1.05);
      c.pin(i, up.x + (fx - up.x) * k, up.y + (fy - up.y) * k, up.z + (fz - up.z) * k, teleport);
    }
    // Sails are cut fuller than the frame they're set on, so they can belly.
    c.setRestScale(1.06, Math.max(0.05, d) * 1.07);
  }

  private pinForeAft(s: SailState, spec: ForeAftSailSpec, teleport: boolean): void {
    const c = s.cloth;
    const g = this.sheet[spec.id] ?? 0;
    const d = s.deploy;
    if (spec.peak) {
      // Gaff sail: luff to the mast, head to the gaff, foot to the boom.
      for (let r = 0; r < c.rows; r++) {
        const v = r / (c.rows - 1);
        const p = foreAftPoint(spec, 0, v, g, 1, this.tmp);
        c.pin(r * c.cols, p.x, p.y, p.z, teleport);
      }
      const top = (c.rows - 1) * c.cols;
      for (let col = 1; col < c.cols; col++) {
        const u = col / (c.cols - 1);
        const p = foreAftPoint(spec, u, 1, g, 1, this.tmp);
        c.pin(top + col, p.x, p.y, p.z, teleport);
        if (d > 0.35) {
          const q = foreAftPoint(spec, u * Math.min(1, d), 0, g, 1, this.tmp);
          c.pin(col, q.x, q.y, q.z, teleport);
        } else {
          c.invMass[col] = 1 / c.mass[col]!;
        }
      }
      c.setRestScale(Math.max(0.08, d) * 1.06, 1.04);
    } else {
      // Headsail: hanked to its stay, hauled down toward the tack.
      for (let r = 0; r < c.rows; r++) {
        const v = r / (c.rows - 1);
        for (let col = 0; col < (r === c.rows - 1 ? c.cols : 1); col++) {
          const p = foreAftPoint(spec, col / (c.cols - 1), v, g, Math.max(0.04, d), this.tmp);
          c.pin(r * c.cols + col, p.x, p.y, p.z, teleport);
        }
      }
      const p = foreAftPoint(spec, 1, 0, g, Math.max(0.04, d), this.tmp);
      c.pin(c.cols - 1, p.x, p.y, p.z, teleport);
      c.setRestScale(Math.max(0.1, Math.min(1, d * 1.2)) * 1.03, Math.max(0.04, d) * 1.01);
    }
  }

  /**
   * Advance sail handling and cloth, and compute each sail's force.
   * @param wind apparent wind at a ship-local height (writes ship-local vector)
   * @param g effective gravity in ship space
   */
  update(dt: number, wind: (h: number, out: Vec3) => Vec3, g: Vec3, turbulence: number): void {
    this.time += dt;
    for (const s of this.sails) {
      if (s.lost) continue;
      // Setting and taking in sail needs hands aloft.
      const hands = 0.15 + 0.85 * this.handling;
      const rate = (s.target > s.deploy ? 0.075 : 0.11) * hands;
      s.deploy += clamp(s.target - s.deploy, -rate * dt, rate * dt);
    }
    this.strikeT = clamp(this.strikeT + (this.struck ? dt : -dt) * 0.25, 0, 1);
    this.trim(dt, wind);

    const w = this.tmp;
    for (const s of this.sails) {
      const d = s.deploy;
      const aw = wind(s.ce.y, w);
      const square = s.spec.kind === 'square';
      const n = this.sailNormal(s);
      if (s.lost) {
        s.force.set(0, 0, 0);
        s.filled = 0;
        continue;
      }
      // Shot-through canvas spills wind.
      const intact = Math.pow(s.cloth.health, 1.5);
      s.aoa = sailForce(aw.x, aw.z, n.x, n.z, s.area * d * (0.35 + 0.65 * d) * intact, square, s.force);
      const an = (aw.x * n.x + aw.z * n.z) / (Math.hypot(aw.x, aw.z) || 1);
      s.filled = d > 0.1 ? an : 0;
    }

    this.pinAll(false);
    for (const s of this.sails) {
      if (s.lost || (this.clothSkip > 0 && (this.frame % (this.clothSkip + 1)) !== 0)) continue;
      const aw = wind(s.ce.y, w);
      const idle = s.deploy < 0.04;
      const passes = this.clothCoarse ? (idle ? 2 : 4) : idle ? 3 : 6;
      s.cloth.step(Math.min(dt, 1 / 30), g.x, g.y, g.z, idle ? aw.x * 0.2 : aw.x, aw.y, idle ? aw.z * 0.2 : aw.z, this.time + s.ce.x, turbulence, passes);
    }
    for (const f of this.flags) {
      if (f.lost || (this.clothSkip > 0 && (this.frame % (this.clothSkip + 1)) !== 0)) continue;
      const aw = wind(f.top.y, w);
      f.cloth.step(Math.min(dt, 1 / 30), g.x, g.y, g.z, aw.x, aw.y, aw.z, this.time * 1.7, turbulence + 0.4, 4);
    }
    this.frame++;
  }

  /** Solve the cloth in fewer passes (distant ships: the shape needn't be so exact). */
  clothCoarse = false;

  /** Update cloth only every (n+1)th step (distant ships). */
  clothSkip = 0;

  /** Number of updates so far (renderers re-upload cloth only when it moved). */
  get updates(): number {
    return this.frame;
  }
  private frame = 0;

  /** Horizontal normal of a sail's working face for its current trim. */
  sailNormal(s: SailState): Vec3 {
    if (s.spec.kind === 'square') {
      const b = this.brace[yard(s.spec.yard).mast];
      return this.tmpF.set(Math.cos(b), 0, -Math.sin(b));
    }
    const g = this.sheet[s.spec.id] ?? 0;
    return this.tmpF.set(Math.sin(g), 0, Math.cos(g));
  }

  private trim(dt: number, wind: (h: number, out: Vec3) => Vec3): void {
    const f = this.trimF;
    const w = this.trimW;
    for (const m of MASTS) {
      if (!this.masts.includes(m.id)) continue;
      const sails = this.byMast[m.id];
      let target = this.manualTrim * MAX_BRACE;
      if (this.autoTrim) {
        const aw = wind(m.lowerTop, w);
        let best = -Infinity;
        for (let i = 0; i <= 24; i++) {
          const b = -MAX_BRACE + (2 * MAX_BRACE * i) / 24;
          sailForce(aw.x, aw.z, Math.cos(b), -Math.sin(b), 100, true, f);
          if (f.x > best + 1e-6) {
            best = f.x;
            target = b;
          }
        }
        if (sails.every((s) => s.deploy < 0.02 || s.lost)) target = 0;
      }
      this.braceTarget[m.id] = target;
      this.brace[m.id] += clamp(target - this.brace[m.id], -0.1 * dt, 0.1 * dt);
    }
    for (const s of this.sails) {
      if (s.spec.kind !== 'foreaft') continue;
      const id = s.spec.id;
      let target = this.manualTrim * MAX_SHEET;
      if (this.autoTrim) {
        const aw = wind(s.ce.y, w);
        let best = -Infinity;
        for (let i = 0; i <= 26; i++) {
          const gm = -MAX_SHEET + (2 * MAX_SHEET * i) / 26;
          sailForce(aw.x, aw.z, Math.sin(gm), Math.cos(gm), 100, false, f);
          // Fore-and-aft sails can only set to leeward of their luff.
          const lee = aw.z * gm >= 0 ? 1 : 0;
          const score = f.x * lee - (1 - lee) * 1e6;
          if (score > best) {
            best = score;
            target = gm;
          }
        }
      }
      this.sheetTarget[id] = target;
      this.sheet[id] = this.sheet[id]! + clamp(target - this.sheet[id]!, -0.35 * dt, 0.35 * dt);
    }
  }
}

function mastCapsule(id: MastId, y0: number, y1: number): Capsule {
  const m = mast(id);
  return { ax: mastXAt(m, y0), ay: y0, az: 0, bx: mastXAt(m, y1), by: y1, bz: 0, r: m.radius + 0.12 };
}

/**
 * Point on a fore-and-aft sail at (u across from luff, v up the luff), with
 * the sheet eased by angle g and the sail hoisted to fraction d.
 */
function foreAftPoint(spec: ForeAftSailSpec, u: number, v: number, g: number, d: number, out: Vec3): Vec3 {
  const T = new Vec3(spec.tack.x, spec.tack.y, 0);
  const H = new Vec3(spec.head.x, spec.head.y, 0);
  if (spec.peak) {
    // Gaff sail: bilinear between luff (mast) and leech (peak..clew), boom and
    // gaff swing about the mast by g.
    const lx = T.x + (H.x - T.x) * v, ly = T.y + (H.y - T.y) * v;
    const ex = spec.clew.x + (spec.peak.x - spec.clew.x) * v;
    const ey = spec.clew.y + (spec.peak.y - spec.clew.y) * v;
    const px = lx + (ex - lx) * u;
    const py = ly + (ey - ly) * u;
    return rotateAbout(px, 0, lx, 0, g, out, py);
  }
  // Headsail: the head slides down the stay as the sail is lowered.
  const Hd = new Vec3(T.x + (H.x - T.x) * d, T.y + (H.y - T.y) * d, 0);
  const C = new Vec3(spec.clew.x, spec.clew.y, 0);
  const Cd = new Vec3(T.x + (C.x - T.x) * Math.max(d, 0.15), T.y + (C.y - T.y) * Math.max(d, 0.15), 0);
  rotateAboutAxis(Cd, T, H, g, Cd);
  const lx = T.x + (Hd.x - T.x) * v, ly = T.y + (Hd.y - T.y) * v, lz = 0;
  const ex = Cd.x + (Hd.x - Cd.x) * v, ey = Cd.y + (Hd.y - Cd.y) * v, ez = Cd.z * (1 - v);
  return out.set(lx + (ex - lx) * u, ly + (ey - ly) * u, lz + (ez - lz) * u);
}

/** Area and centre of effort of a sail at full hoist (flat, sheet amidships). */
export function sailGeometry(spec: SailSpec): { area: number; ce: Vec3 } {
  let corners: { x: number; y: number; z: number }[];
  if (spec.kind === 'square') {
    const y = yard(spec.yard);
    const m = mast(y.mast);
    const xt = mastXAt(m, y.y) + SLING_OFFSET;
    const xb = mastXAt(m, spec.footY) + SLING_OFFSET;
    // Project onto the athwartships plane: area = trapezoid.
    const h = y.y - spec.footY;
    const area = ((y.half + spec.footHalf) * h);
    const cy = spec.footY + (h * (2 * y.half + spec.footHalf)) / (3 * (y.half + spec.footHalf));
    return { area, ce: new Vec3((xt + xb) / 2, cy, 0) };
  }
  if (spec.peak) corners = [spec.tack, spec.clew, spec.peak, spec.head].map((p) => ({ x: p.x, y: p.y, z: 0 }));
  else corners = [spec.tack, spec.clew, spec.head].map((p) => ({ x: p.x, y: p.y, z: 0 }));
  // Polygon area / centroid in the x-y plane.
  let a = 0, cx = 0, cy = 0;
  for (let i = 0; i < corners.length; i++) {
    const p = corners[i]!, q = corners[(i + 1) % corners.length]!;
    const cr = p.x * q.y - q.x * p.y;
    a += cr;
    cx += (p.x + q.x) * cr;
    cy += (p.y + q.y) * cr;
  }
  a *= 0.5;
  return { area: Math.abs(a), ce: new Vec3(cx / (6 * a), cy / (6 * a), 0) };
}
